/* eslint-disable max-lines-per-function */
/* eslint-disable functional/prefer-readonly-type */
import React, { useEffect } from 'react'
import { State, useHookstate } from '@hookstate/core'
import {
  getAutoCompleteOptionsWithInterestPoint,
  getCityIdByName,
  getDistrictIdByName,
  getPlaceDetails,
  LocationPrediction,
} from '~/utils/location'
import { FetchAutoComplete } from '../FetchAutoComplete'
import { LocationOn } from '@mui/icons-material'
import { Typography } from '@mui/material'
import { useLang } from '~/hooks/useLang'

export type Type = 'dispatch' | 'entry' | 'interestPoint'

export type LocationState = {
  readonly id: State<number | string | null>
  readonly cityId: State<number | string | null>
  readonly districtId?: State<number | string | null>
  readonly locationValue: State<string | null>
  readonly type: Type
}

interface LocalityTextFieldProps {
  readonly readonly?: boolean
  readonly locationState: LocationState
  readonly isInvalidRequest?: boolean
  readonly isValid: boolean
}

interface IAddress {
  readonly city?: string
  readonly secondary?: string
  readonly town?: string
  readonly district?: string
}

export function LocalityTextFieldCrud({
  readonly = false,
  locationState,
  isInvalidRequest,
  isValid,
}: LocalityTextFieldProps) {
  const { id, cityId, locationValue, type, districtId } = locationState
  const showError = useHookstate(true)
  const { translate } = useLang()

  const setDistrictState = async (address: IAddress) => {
    if (!address) return
    const district = address.district
    if (district && districtId) {
      const id = await getDistrictIdByName(district)
      districtId.set(id)
    }
  }

  const setCityStates = async (address: IAddress) => {
    if (!address) return
    const city = address.city || address.town || null
    if (city) {
      const id = await getCityIdByName(city)
      cityId.set(id)
    }
  }

  const getAutoCompleteFunction = (newInput: string) => {
    return getAutoCompleteOptionsWithInterestPoint(newInput)
  }

  useEffect(() => {
    validateLocation()
  }, [isInvalidRequest, locationValue])

  const orderBy = (options: any[]) => {
    return options.map((option) => ({
      priority: option.from_interest_point
        ? translate('Pontos de Interesse')
        : translate('Endereços'),
      ...option,
    }))
  }

  const setLocationFunc = async (
    option: LocationPrediction
  ): Promise<{ readonly ended: boolean; readonly value: string }> => {
    const idOrNull = id.get()
    if (idOrNull) {
      try {
        let locationDetail: IAddress | null = null
        if (option?.placeId) {
          locationDetail = await getPlaceDetails(option.placeId)
        }
        // Se não tem placeId, use os dados disponíveis na opção
        if (!locationDetail && option) {
          locationDetail = {
            city: (option as any).main ?? undefined,
            district: undefined,
          }
        }

        await Promise.all([
          setCityStates(locationDetail!),
          setDistrictState(locationDetail!),
        ])

        return {
          ended: true,
          value: option?.description ?? option.main,
        }
      } catch (err) {
        throw err
      }
    }
    return { ended: false, value: '' }
  }

  const validateLocation = () => {
    if (locationValue.get()?.length !== 0) {
      showError.set(false)
    } else {
      showError.set(true)
    }
  }

  if (!readonly) {
    return (
      <FetchAutoComplete<LocationPrediction>
        fetchFunc={getAutoCompleteFunction}
        setFunc={setLocationFunc}
        noOptionsString={'No Locations, yet...'}
        optionKey={'main'}
        stateValue={locationValue}
        startIcon
        startIconElement={<LocationOn fontSize="small" />}
        autoCompleteProps={{
          getOptionLabel: (option) => {
            return option?.description ? option?.description : option.main
          },
          placeholder: 'Location',
          size: 'small',
          errorMsg: false,
          filterOptions: (original) => orderBy(original),
          groupBy: (option) => {
            return option.priority
          },
          renderGroup: (params) => (
            <div key={params.key}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 'bold', color: 'primary.main', px: 1, py: 0.5 }}
              >
                {params.group}
              </Typography>
              {params.children}
            </div>
          ),
        }}
        isValid={isValid}
        error={isInvalidRequest && showError.get()}
      />
    )
  } else {
    return (
      <Typography>
        {translate('Location')}
:
{' '}
        {locationValue.get() ?? translate('No information')}
      </Typography>
    )
  }
}